// src/screens/LoginScreen.tsx
//
// Pilot-readiness P3 (founder B1) — username/password login; the logged-out
// default screen (replaces the invite-code gate for students). Students
// without an account register via their school's /join/<slug> link.

import { useState } from 'react';
import { authApi } from '../api/auth';
import { ApiError } from '../api/client';
import { useTokens } from '../state/theme';
import { PasswordField } from '../components/PasswordField';

const ERROR_COPY: Record<string, string> = {
  invalid_credentials: 'Nome utente o password non corretti.',
  rate_limited: 'Troppi tentativi. Aspetta 15 minuti e riprova.',
  validation_error: 'Controlla i campi e riprova.',
};

export const RECOVERY_ACCEPTED_COPY =
  'Richiesta ricevuta. Se i dati inseriti sono validi, ora puoi accedere con la nuova password.';

export function LoginScreen({ onAuthed }: { onAuthed: () => void }) {
  const tokens = useTokens();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryUsername, setRecoveryUsername] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [recoveryAccepted, setRecoveryAccepted] = useState(false);

  const canSubmit = username.trim().length >= 1 && password.length >= 1 && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      await authApi.loginStudent({ username: username.trim(), password });
      onAuthed();
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : undefined;
      setError(ERROR_COPY[code ?? ''] ?? 'Non riesco ad accedere. Riprova tra poco.');
    } finally {
      setLoading(false);
    }
  };

  const handleRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recoveryUsername.trim() || !recoveryCode.trim() || newPassword.length < 8 || recoveryLoading) return;
    setRecoveryLoading(true);
    setRecoveryError(null);
    try {
      await authApi.recoverStudent({
        username: recoveryUsername.trim(),
        code: recoveryCode.trim(),
        newPassword,
      });
      setRecoveryAccepted(true);
      setRecoveryCode('');
      setNewPassword('');
    } catch {
      setRecoveryError('Non riesco a inviare la richiesta. Riprova tra poco.');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const openRecovery = () => {
    setRecoveryUsername(username.trim());
    setRecoveryOpen(true);
    setRecoveryAccepted(false);
    setRecoveryError(null);
  };

  const closeRecovery = () => {
    setRecoveryOpen(false);
    setRecoveryCode('');
    setNewPassword('');
    setRecoveryError(null);
    setRecoveryAccepted(false);
  };

  const fieldStyle: React.CSSProperties = {
    background: tokens.surface,
    border: `1px solid ${tokens.lineStrong}`,
    borderRadius: tokens.radius.md,
    padding: '10px 14px',
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  };
  const inputStyle: React.CSSProperties = {
    border: 'none', outline: 'none', background: 'transparent', width: '100%',
    color: tokens.text, fontFamily: tokens.fonts.body, fontSize: 15, minHeight: 24,
  };
  const eyebrowStyle: React.CSSProperties = {
    display: 'block', fontFamily: tokens.fonts.body, fontSize: 10.5, fontWeight: 800,
    letterSpacing: 0.9, textTransform: 'uppercase', color: tokens.accentInk, marginTop: 12,
  };

  return (
    <div style={{ maxWidth: 460, margin: '0 auto', padding: '24px 16px', fontFamily: tokens.fonts.body, color: tokens.text }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>Bentornato su Il Prof</h1>
      <p style={{ color: tokens.textFaint, marginTop: 0, fontSize: 13.5 }}>
        Accedi con il tuo nome utente e la tua password.
      </p>

      {!recoveryOpen ? <form onSubmit={handleSubmit}>
        <span style={eyebrowStyle}>Nome utente</span>
        <div style={fieldStyle}>
          <input style={inputStyle} value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="es. giulia.rossi" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            autoComplete="username" disabled={loading} autoFocus aria-label="Nome utente" />
        </div>

        <span style={eyebrowStyle}>Password</span>
        <PasswordField value={password} onChange={setPassword}
          autoComplete="current-password" disabled={loading} />

        {error && (
          <p role="alert" style={{ color: tokens.wrongText, fontSize: 13, marginTop: 10 }}>{error}</p>
        )}

        <button type="submit" disabled={!canSubmit} data-testid="login-submit"
          style={{
            width: '100%', marginTop: 16, minHeight: 48, borderRadius: tokens.radius.md, border: 'none',
            background: canSubmit ? tokens.accent : tokens.surface,
            color: canSubmit ? '#fff' : tokens.textFaint,
            fontFamily: tokens.fonts.body, fontSize: 15, fontWeight: 700,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}>
          {loading ? 'Un attimo…' : 'Accedi'}
        </button>
      </form> : (
        <form onSubmit={handleRecovery} aria-label="Recupera password">
          <h2 style={{ fontSize: 18, margin: '18px 0 4px' }}>Recupera la password</h2>
          <p style={{ color: tokens.textFaint, margin: '0 0 10px', fontSize: 13.5 }}>
            Usa uno dei codici del kit salvato al momento dell’iscrizione.
          </p>

          <span style={eyebrowStyle}>Nome utente</span>
          <div style={fieldStyle}>
            <input style={inputStyle} value={recoveryUsername} onChange={(e) => setRecoveryUsername(e.target.value)}
              autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username"
              disabled={recoveryLoading || recoveryAccepted} aria-label="Nome utente per il recupero" />
          </div>

          <span style={eyebrowStyle}>Codice di recupero</span>
          <div style={fieldStyle}>
            <input style={inputStyle} value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)}
              autoCapitalize="characters" autoCorrect="off" spellCheck={false} autoComplete="one-time-code"
              disabled={recoveryLoading || recoveryAccepted} aria-label="Codice di recupero" />
          </div>

          <span style={eyebrowStyle}>Nuova password</span>
          <PasswordField value={newPassword} onChange={setNewPassword} ariaLabel="Nuova password"
            placeholder="Almeno 8 caratteri" autoComplete="new-password"
            disabled={recoveryLoading || recoveryAccepted} />

          {recoveryError && <p role="alert" style={{ color: tokens.wrongText, fontSize: 13 }}>{recoveryError}</p>}
          {recoveryAccepted && <p role="status" style={{ color: tokens.text, fontSize: 13.5 }}>{RECOVERY_ACCEPTED_COPY}</p>}

          {!recoveryAccepted && (
            <button type="submit" disabled={recoveryLoading || !recoveryUsername.trim() || !recoveryCode.trim() || newPassword.length < 8}
              style={{ width: '100%', marginTop: 16, minHeight: 48, border: 'none', borderRadius: tokens.radius.md,
                background: tokens.accent, color: '#fff', fontFamily: tokens.fonts.body, fontWeight: 700 }}>
              {recoveryLoading ? 'Un attimo…' : 'Aggiorna password'}
            </button>
          )}
          <button type="button" onClick={closeRecovery} style={{
            width: '100%', marginTop: 8, minHeight: 44, border: `1px solid ${tokens.lineStrong}`,
            borderRadius: tokens.radius.md, background: tokens.surface, color: tokens.text,
            fontFamily: tokens.fonts.body, fontWeight: 600,
          }}>
            Torna all’accesso
          </button>
        </form>
      )}

      {/* B1 — no more codes: new students arrive ONLY via the school link. */}
      <p data-testid="login-join-hint" style={{ marginTop: 16, fontSize: 13, color: tokens.textFaint, lineHeight: 1.5 }}>
        Non hai ancora un account? Registrati con il <strong>link della tua autoscuola</strong> —
        chiedilo al tuo istruttore.
      </p>
      {!recoveryOpen && (
        <button type="button" onClick={openRecovery} style={{
          minHeight: 44, padding: 0, border: 'none', background: 'transparent',
          color: tokens.accentInk, fontFamily: tokens.fonts.body, fontSize: 13, fontWeight: 700,
        }}>
          Password dimenticata?
        </button>
      )}
    </div>
  );
}
